import React from "react";
import { Button, Stack } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import PropTypes from "prop-types";
import JumboRqTabs from "@jumbo/components/JumboReactQuery/JumboRqTabs";

// Map for icons
const iconMap = {
  ArrowBack: ArrowBackIcon,
  Add: AddIcon,
  Edit: EditIcon,
};

// HOC to enhance a tabs component with color, variant, and icon support
const withStyledTabs = (WrappedComponent) => {
  const StyledTabs = ({
    data,
    map,
    primaryKey,
    onChange,
    sx,
    buttonColor = "primary",
    buttonVariant = "contained",
    ...restProps
  }) => {
    const [activeTab, setActiveTab] = React.useState(null);

    // Handle tab click
    const handleTabClick = (item) => {
      setActiveTab(item);
      onChange(item);
    };

    // Set initial active tab
    React.useEffect(() => {
      if (data && data.length > 0) {
        if (!activeTab) {
          setActiveTab(data[0]);
        }
      }
    }, [data, activeTab]);

    return (
      <Stack direction={"row"} spacing={1} sx={sx}>
        {data &&
          data.map((item, index) => {
            const IconComponent = item.icon && iconMap[item.icon];
            const isActive =
              activeTab && item[primaryKey] === activeTab[primaryKey];

            return (
              <Button
                key={index}
                size={"small"}
                variant={
                  isActive
                    ? item.variant || buttonVariant
                    : item.inactiveVariant || "outlined"
                }
                color={item.color || buttonColor}
                onClick={() => handleTabClick(item)}
                startIcon={
                  IconComponent ? (
                    <IconComponent
                      sx={{
                        color: (isActive ? item.color : "inherit") || "inherit",
                      }}
                    />
                  ) : null
                }
                sx={{
                  ...(item.color &&
                    item.variant === "contained" && {
                      backgroundColor: (theme) =>
                        theme.palette[item.color]?.main,
                      color: (theme) => theme.palette[item.color]?.contrastText,
                      "&:hover": {
                        backgroundColor: (theme) =>
                          theme.palette[item.color]?.dark,
                      },
                    }),
                }}
              >
                {item[map.label]}
              </Button>
            );
          })}
      </Stack>
    );
  };

  // Forward props to WrappedComponent for query-based data
  return (props) => {
    if (props.data) {
      // If data is provided (static tabs like in ContentHeader), use StyledTabs
      return <StyledTabs {...props} />;
    }
    // Otherwise, pass through to WrappedComponent for query-based tabs
    return <WrappedComponent {...props} />;
  };
};

// Define prop types for the enhanced component
withStyledTabs.propTypes = {
  service: PropTypes.func,
  queryOptions: PropTypes.shape({
    queryKey: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    queryParams: PropTypes.object,
    dataKey: PropTypes.string,
  }),
  data: PropTypes.array,
  primaryKey: PropTypes.string.isRequired,
  map: PropTypes.shape({
    label: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  sx: PropTypes.object,
  buttonColor: PropTypes.string,
  buttonVariant: PropTypes.string,
};

// Export the enhanced component
export const StyledJumboRqTabs = withStyledTabs(JumboRqTabs);
export default withStyledTabs;
